/**
 * hooks/withdrawals/useWithdrawalsList.ts
 *
 * List hook for consumer's manual withdrawals history.
 * Registered in listHooks as "manual_withdrawals".
 */

import {
  ArrowUpCircleOutline,
  TimeOutline,
} from "@raresail/react-ionicons";
import {
  IconBox,
  ListItemWrapper,
  StatusTag,
} from "../../components/elements/Elements";
import { API_MANUAL_WITHDRAWALS } from "../../constants/apis";
import { MANUAL_WITHDRAWALS, NEW_MANUAL_WITHDRAWAL, SELECT } from "../../constants/constants";
import { searchFilterField } from "../../constants/options";
import {
  addSelectOption,
  currencyFormat,
  formatDate,
  formatDateTime,
} from "../../utils/helpers";
import { openBottomSheet, openModalForm } from "../../utils/modalUtils";

export const useWithdrawalsList = () => {
  const filters = [
    searchFilterField,
    {
      field: "status",
      type: SELECT,
      options: ["pending", "completed", "rejected"].map((v) =>
        addSelectOption(v)
      ),
    },
  ];

  const viewData = (data: any) => {
    const {
      amount,
      currency,
      status,
      destination,
      proof_of_payment,
      rejection_reason,
      created_at,
      approved_at,
    } = data || {};

    return {
      amount: currencyFormat(amount, currency),
      status: <StatusTag status={status} />,
      destination: destination || "—",
      ...(proof_of_payment
        ? {
            proof: (
              <a
                href={proof_of_payment}
                target="_blank"
                rel="noopener noreferrer"
                className="main semiBold font12"
              >
                View POP ↗
              </a>
            ),
          }
        : {}),
      ...(rejection_reason ? { rejection_reason } : {}),
      requested: formatDateTime(created_at),
      ...(approved_at ? { processed: formatDateTime(approved_at) } : {}),
    };
  };

  const ListItem = ({ data }: { data: any }) => {
    const { amount, currency, status, destination, created_at } = data || {};

    return (
      <ListItemWrapper
        className="flexNullCenter gap15"
        onClick={() =>
          openBottomSheet({
            component: "details",
            title: "Withdrawal Details",
            source: MANUAL_WITHDRAWALS,
            data,
          })
        }
      >
        <IconBox icon={ArrowUpCircleOutline} size={20} />

        <div className="flexGrow">
          <div className="font13 semiBold textDark">
            {currencyFormat(amount, currency)}
          </div>
          <div className="font11 grey ellipsis">
            {destination || "Manual withdrawal"}
          </div>
          <div className="font10 grey flexNullCenter gap5 mt3">
            <TimeOutline fontSize={11} />
            {formatDate(created_at)}
          </div>
        </div>

        <div className="textEnd">
          <StatusTag status={status} />
        </div>
      </ListItemWrapper>
    );
  };

  return {
    ListItem,
    title: "My Withdrawals",
    subtitle: "Manual withdrawal history",
    api: API_MANUAL_WITHDRAWALS,
    filters,
    viewData,
    action: {
      onClick: () => openModalForm({ source: NEW_MANUAL_WITHDRAWAL }),
    },
  };
};